import React, { useState, useEffect } from "react";
import {
    getFirestore, collection, addDoc, query, orderBy, getDocs, serverTimestamp,
    doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Container,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
    Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MuiAlert from '@mui/material/Alert';
import moment from "moment";
import { getCuadrillasBasic } from "../../services/cuadrillasService";

const currentUser = JSON.parse(localStorage.getItem("currentUser"));
const firestore = getFirestore();
const isAdmin = currentUser?.rol === 'admin';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₲ 0';
    return new Intl.NumberFormat('es-PY', {
        style: 'currency', currency: 'PYG',
        minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
};

const emptyForm = { fecha: null, cuadrilla: null, monto: '', observaciones: '' };
const emptyFilters = { desde: null, hasta: null, cuadrilla: null };

const Adelantos = () => {
    const [form, setForm] = useState(emptyForm);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, text: '', type: 'success' });
    const [editDialog, setEditDialog] = useState({ open: false, registro: null });
    const [editForm, setEditForm] = useState(emptyForm);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, registro: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(true);
    const [filters, setFilters] = useState(emptyFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const loadRegistros = async () => {
        setLoadingList(true);
        try {
            const q = query(collection(firestore, 'adelantos'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            setRegistros(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        getCuadrillasBasic(setCuadrillas);
        loadRegistros();
    }, []);

    // --- Filtering ---
    const filteredRegistros = registros.filter(r => {
        if (filters.desde && r.fecha && r.fecha < moment(filters.desde).format("YYYYMMDD")) return false;
        if (filters.hasta && r.fecha && r.fecha > moment(filters.hasta).format("YYYYMMDD")) return false;
        if (filters.cuadrilla && r.idCuadrilla !== filters.cuadrilla.id) return false;
        return true;
    });

    const totalFiltrado = filteredRegistros.reduce((acc, r) => acc + (r.monto || 0), 0);
    const paginatedRows = filteredRegistros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const hasActiveFilters = filters.desde || filters.hasta || filters.cuadrilla;

    const handleClearFilters = () => {
        setFilters(emptyFilters);
        setPage(0);
    };

    // --- Submit ---
    const handleSubmit = async () => {
        if (!form.fecha || !form.cuadrilla || !form.monto) {
            setSnackbar({ open: true, text: 'Fecha, cuadrilla y monto son obligatorios', type: 'error' });
            return;
        }
        setLoading(true);
        try {
            await addDoc(collection(firestore, 'adelantos'), {
                fecha: moment(form.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(form.fecha).format("DD/MM/YYYY"),
                idCuadrilla: form.cuadrilla.id,
                cuadrilla: form.cuadrilla.numero,
                monto: parseFloat(form.monto),
                observaciones: form.observaciones || '',
                createdAt: serverTimestamp()
            });
            setSnackbar({ open: true, text: 'Adelanto registrado correctamente', type: 'success' });
            setForm(emptyForm);
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al guardar el adelanto', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // --- Edit ---
    const openEdit = (r) => {
        const fechaMoment = r.fecha ? moment(r.fecha, "YYYYMMDD") : null;
        const cuadrillaObj = cuadrillas.find(c => c.id === r.idCuadrilla)
            || (r.idCuadrilla ? { id: r.idCuadrilla, numero: r.cuadrilla } : null);
        setEditForm({ fecha: fechaMoment, cuadrilla: cuadrillaObj, monto: r.monto ?? '', observaciones: r.observaciones || '' });
        setEditDialog({ open: true, registro: r });
    };
    const closeEdit = () => { setEditDialog({ open: false, registro: null }); setEditForm(emptyForm); };
    const handleEditSave = async () => {
        if (!editForm.fecha || !editForm.cuadrilla || !editForm.monto) {
            setSnackbar({ open: true, text: 'Fecha, cuadrilla y monto son obligatorios', type: 'error' });
            return;
        }
        setEditLoading(true);
        try {
            await updateDoc(doc(firestore, 'adelantos', editDialog.registro.id), {
                fecha: moment(editForm.fecha).format("YYYYMMDD"),
                fechaDisplay: moment(editForm.fecha).format("DD/MM/YYYY"),
                idCuadrilla: editForm.cuadrilla.id,
                cuadrilla: editForm.cuadrilla.numero,
                monto: parseFloat(editForm.monto),
                observaciones: editForm.observaciones || ''
            });
            setSnackbar({ open: true, text: 'Adelanto actualizado correctamente', type: 'success' });
            closeEdit();
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al actualizar el adelanto', type: 'error' });
        } finally {
            setEditLoading(false);
        }
    };

    // --- Delete ---
    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await deleteDoc(doc(firestore, 'adelantos', deleteDialog.registro.id));
            setSnackbar({ open: true, text: 'Adelanto eliminado correctamente', type: 'success' });
            setDeleteDialog({ open: false, registro: null });
            loadRegistros();
        } catch (e) {
            setSnackbar({ open: true, text: 'Error al eliminar el adelanto', type: 'error' });
        } finally {
            setDeleteLoading(false);
        }
    };

    const isValid = form.fecha && form.cuadrilla && form.monto;
    const isEditValid = editForm.fecha && editForm.cuadrilla && editForm.monto;

    return (
        <Container maxWidth="md" sx={{ mt: 2 }}>

            {/* --- Nuevo Adelanto (colapsable) --- */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => setFormOpen(o => !o)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon color="primary" />
                        <Typography variant="h6">Nuevo Adelanto</Typography>
                    </Box>
                    <IconButton size="small">{formOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={formOpen}>
                    <Divider />
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Fecha"
                                    inputFormat="DD/MM/YYYY"
                                    value={form.fecha}
                                    onChange={(v) => setForm(p => ({ ...p, fecha: v }))}
                                    renderInput={(params) => <TextField {...params} sx={{ flex: 1, minWidth: 180 }} />}
                                />
                            </LocalizationProvider>
                            <Autocomplete
                                options={cuadrillas}
                                getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                                value={form.cuadrilla}
                                onChange={(_, v) => setForm(p => ({ ...p, cuadrilla: v }))}
                                sx={{ flex: 1, minWidth: 220 }}
                                renderInput={(params) => <TextField {...params} label="Cuadrilla" />}
                            />
                        </Box>
                        <TextField
                            label="Monto (₲)"
                            type="number"
                            value={form.monto}
                            onChange={(e) => setForm(p => ({ ...p, monto: e.target.value }))}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Observaciones"
                            multiline
                            rows={2}
                            value={form.observaciones}
                            onChange={(e) => setForm(p => ({ ...p, observaciones: e.target.value }))}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!isValid || loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {loading ? 'Guardando...' : 'Registrar Adelanto'}
                        </Button>
                    </Box>
                </Collapse>
            </Paper>

            {/* --- Historial --- */}
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Historial de Adelantos</Typography>
                    {hasActiveFilters && (
                        <Chip
                            label="Limpiar filtros"
                            onDelete={handleClearFilters}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>

                {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                    <FilterListIcon color="action" />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Desde"
                            inputFormat="DD/MM/YYYY"
                            value={filters.desde}
                            onChange={(v) => { setFilters(f => ({ ...f, desde: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                        <DatePicker
                            label="Hasta"
                            inputFormat="DD/MM/YYYY"
                            value={filters.hasta}
                            onChange={(v) => { setFilters(f => ({ ...f, hasta: v })); setPage(0); }}
                            renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                    </LocalizationProvider>
                    <Autocomplete
                        options={cuadrillas}
                        getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                        value={filters.cuadrilla}
                        onChange={(_, v) => { setFilters(f => ({ ...f, cuadrilla: v })); setPage(0); }}
                        sx={{ width: 240 }}
                        renderInput={(params) => <TextField {...params} label="Cuadrilla" size="small" />}
                    />
                </Box>

                {loadingList ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Fecha</strong></TableCell>
                                        <TableCell><strong>Cuadrilla</strong></TableCell>
                                        <TableCell align="right"><strong>Monto</strong></TableCell>
                                        <TableCell><strong>Observaciones</strong></TableCell>
                                        {isAdmin && <TableCell align="center"><strong>Acciones</strong></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                                                {hasActiveFilters ? 'No hay registros con los filtros aplicados' : 'No hay registros'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedRows.map((r) => (
                                            <TableRow key={r.id} hover>
                                                <TableCell>{r.fechaDisplay || r.fecha}</TableCell>
                                                <TableCell>{r.cuadrilla}</TableCell>
                                                <TableCell align="right">{formatCurrency(r.monto)}</TableCell>
                                                <TableCell>{r.observaciones}</TableCell>
                                                {isAdmin && (
                                                    <TableCell align="center">
                                                        <Tooltip title="Editar">
                                                            <IconButton size="small" onClick={() => openEdit(r)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Eliminar">
                                                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, registro: r })}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Total + Paginación */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                                Total{hasActiveFilters ? ' (filtrado)' : ''}: <strong>{formatCurrency(totalFiltrado)}</strong>
                                {hasActiveFilters && ` — ${filteredRegistros.length} de ${registros.length} registros`}
                            </Typography>
                            <TablePagination
                                component="div"
                                count={filteredRegistros.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                labelRowsPerPage="Filas:"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                            />
                        </Box>
                    </>
                )}
            </Paper>

            {/* --- Edit Dialog --- */}
            <Dialog open={editDialog.open} onClose={closeEdit} fullWidth maxWidth="sm">
                <DialogTitle>Editar Adelanto</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Fecha"
                                inputFormat="DD/MM/YYYY"
                                value={editForm.fecha}
                                onChange={(v) => setEditForm(p => ({ ...p, fecha: v }))}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                        <Autocomplete
                            options={cuadrillas}
                            getOptionLabel={(o) => o.usuario ? `${o.usuario} - ${o.numero}` : (o.numero || '')}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={editForm.cuadrilla}
                            onChange={(_, v) => setEditForm(p => ({ ...p, cuadrilla: v }))}
                            renderInput={(params) => <TextField {...params} label="Cuadrilla" />}
                        />
                        <TextField
                            label="Monto (₲)"
                            type="number"
                            value={editForm.monto}
                            onChange={(e) => setEditForm(p => ({ ...p, monto: e.target.value }))}
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label="Observaciones"
                            multiline
                            rows={3}
                            value={editForm.observaciones}
                            onChange={(e) => setEditForm(p => ({ ...p, observaciones: e.target.value }))}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={editLoading}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        disabled={!isEditValid || editLoading}
                        startIcon={editLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {editLoading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Delete Dialog --- */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, registro: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el adelanto
                        {deleteDialog.registro && ` de ${deleteDialog.registro.cuadrilla} por ${formatCurrency(deleteDialog.registro.monto)}`}?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>Esta acción no se puede deshacer.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, registro: null })} disabled={deleteLoading}>Cancelar</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {deleteLoading ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.type}>{snackbar.text}</Alert>
            </Snackbar>
        </Container>
    );
};

export default Adelantos;
